/**
 * Image attachments of the contact forms.
 *
 * The field itself is a plain multiple file input, this module only reports
 * what was picked and refuses a file which would be thrown away by the server
 * anyway, so the visitor learns about it before submitting.
 */

import { text } from "../core/i18n";
import { query, queryAll } from "../core/dom";

/** Biggest file the server accepts, in bytes. */
export const MAX_BYTES = 4 * 1024 * 1024;

/** How many files may travel with one form. */
export const MAX_FILES = 4;

const TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/**
 * What one field will take, when it takes something narrower than the rest.
 *
 * A skin is a PNG of a hundred and twenty-eight kilobytes and an attachment
 * is any picture of four megabytes, and a field which answers "not an image"
 * about a perfectly good JPEG is a field lying in order to be reusable.
 */
export interface Limits {
    /** Media types the field accepts. */
    types: readonly string[];

    /** Largest one file may be. */
    maxBytes: number;

    /** What to say about a file of the wrong kind. */
    refuseType: (name: string) => string;

    /** What to say about a file which is too big. */
    refuseSize: (name: string) => string;
}

/** What a field takes when it says nothing about it. */
export const ATTACHMENTS: Limits = {
    types: TYPES,
    maxBytes: MAX_BYTES,
    refuseType: (name) => text("js.upload.not_an_image", "Súbor „{name}“ nie je obrázok.", { name }),
    refuseSize: (name) => text("js.upload.too_large", "Obrázok „{name}“ je väčší než 4 MB.", { name }),
};

/** Return what one field says it takes, or what every field takes. */
export function limitsOf(field: HTMLElement): Limits {
    const types = field.getAttribute("data-upload-types");
    const kilobytes = Number(field.getAttribute("data-upload-max"));

    if (!types || !Number.isFinite(kilobytes) || kilobytes <= 0) {
        return ATTACHMENTS;
    }

    return {
        types: types.split(",").map((one) => one.trim()),
        maxBytes: kilobytes * 1024,
        refuseType: () => text("js.skin.not_a_png", "Skin musí byť obrázok PNG."),
        refuseSize: () => text("js.skin.too_large", "Skin môže mať najviac {count} kB.", { count: kilobytes }),
    };
}

/** Return a readable size, for example `1.4 MB`. */
export function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} kB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Return the complaint about `files`, or an empty string when they are fine. */
export function validateFiles(files: readonly File[], limits: Limits = ATTACHMENTS): string {
    if (files.length > MAX_FILES) {
        return text("js.upload.too_many", "Naraz je možné priložiť najviac {count} obrázky.", {
            count: MAX_FILES,
        });
    }

    for (const file of files) {
        if (!limits.types.includes(file.type)) {
            return limits.refuseType(file.name);
        }

        if (file.size > limits.maxBytes) {
            return limits.refuseSize(file.name);
        }
    }

    return "";
}

/** Return the sentence describing the picked files. */
export function describeFiles(files: readonly File[]): string {
    if (files.length === 0) {
        return "";
    }

    const total = files.reduce((sum, file) => sum + file.size, 0);
    const names = files.map((file) => file.name).join(", ");

    return `${files.length} × ${names} (${formatSize(total)})`;
}

/**
 * Show `file` in `preview`, or clear it when there is nothing to show.
 *
 * The address of the previous picture is handed back before a new one is
 * made: an object URL lives until the page is left, so a field somebody
 * changes their mind about ten times would otherwise hold ten pictures in
 * memory.
 */
export function showPreview(preview: HTMLImageElement, file: File | null): void {
    if (preview.src.startsWith("blob:")) {
        URL.revokeObjectURL(preview.src);
    }

    if (!file) {
        preview.removeAttribute("src");
        preview.hidden = true;

        return;
    }

    preview.src = URL.createObjectURL(file);
    preview.hidden = false;
}

/** Wire a single upload field. */
function initField(field: HTMLElement): void {
    const input = query<HTMLInputElement>("[data-upload-input]", field);
    const hint = query<HTMLElement>("[data-upload-hint]", field);

    if (!input || !hint) {
        return;
    }

    const original = hint.textContent ?? "";
    const preview = query<HTMLImageElement>("[data-upload-preview]", field);
    const limits = limitsOf(field);

    input.addEventListener("change", () => {
        const files = Array.from(input.files ?? []);
        const complaint = validateFiles(files, limits);

        field.classList.toggle("is-invalid", complaint !== "");
        field.classList.toggle("is-filled", complaint === "" && files.length > 0);

        if (complaint) {
            hint.textContent = complaint;
            input.value = "";
            field.classList.remove("is-filled");

            if (preview) {
                showPreview(preview, null);
            }

            return;
        }

        hint.textContent = files.length > 0 ? describeFiles(files) : original;

        // A field which takes several files says so in words; a thumbnail of
        // the first of them would only be half the answer.
        if (preview) {
            showPreview(preview, files.length === 1 ? (files[0] ?? null) : null);
        }
    });
}

/** Wire every upload field of the page. */
export function initUploads(): void {
    for (const field of queryAll<HTMLElement>("[data-upload]")) {
        initField(field);
    }
}
